import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { useIntl } from 'react-intl';

// Components
import CancelOrderModalMobile from '../../../../components/CancelOrderModal/MobileView';
import CancelOrderModalDesktop from '../../../../components/CancelOrderModal/DesktopView';

// State
import { isOrderDoneSelector } from '../../../../state/modules/order/selectors';

// Icons
import TimerIcon from '../../../../icons/timer.svg';

// Utils
import parseISO from '../../../../utils/parseISO';
import isomorphicCookies from '../../../../utils/isomorphicCookies';

// Styles
import styles from './RightPanel.styl';

const cx = classNames.bind(styles);

export default function RightPanel(props) {
  const {
    className,
    orderData: { wanted_time_with_interval, date, time_left, delivery_type, order_status, id },
    getOrderStatus,
  } = props;

  const intl = useIntl();
  const isRealPhone = useSelector(state => state.responsive.isRealPhone);
  const isAuthenticated = useSelector(state => state.user.isAuthenticated);
  const isOrderDone = isOrderDoneSelector({ delivery_type, order_status });

  const timeLeftIntervalRef = useRef(null);
  const cancelIntervalRef = useRef(null);
  // отменить заказ можно только при order_status 0 и 1
  const [isCancelButtonActive, setIsCancelButtonActive] = useState(true);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [cancelTime, setCancelTime] = useState(() => {
    const cancel_timer = isomorphicCookies().get('cancel_timer');

    if (cancel_timer) {
      const a = cancel_timer.split('|');

      if (Number(a[0]) !== id) {
        return 0;
      }

      const cancelTimeLeft = Math.floor(59 - (new Date().getTime() - a[1]) / 1000);
      return cancelTimeLeft >= 0 ? cancelTimeLeft : 0;
    }

    return 0;
  });

  // цвет оставшегося времени
  const dateColor = useMemo(() => {
    const orderDate = parseISO(date).getTime();
    const currentDate = Date.now();
    // сколько прошло секунд между текущим временем и временем заказа
    const orderAndCurrentDiff = Math.round((currentDate - orderDate) / 1000);
    // если это доставка ко времени / прошло меньше половины времени, то цвет зеленый
    return wanted_time_with_interval || orderAndCurrentDiff < time_left ? '#008E6A' : '#F2C94D';
  }, [date, wanted_time_with_interval, time_left]);

  // оставшееся время в минутах/секундах
  const currentDeliveryTime = useMemo(() => {
    if (!timeLeft) {
      return null;
    }

    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    const secondsWithZero = seconds > 9 ? seconds : `0${seconds}`;

    return `${minutes}:${secondsWithZero}`;
  }, [timeLeft]);

  // отменяем заказ
  const onCancelModalOpen = () => {
    setIsCancelModalOpen(true);
  };

  const onCancelModalClose = () => {
    setIsCancelModalOpen(false);
  };

  // если оставшееся время больше 0 и вообще существует, то нужно завести обратный отсчет
  useEffect(() => {
    cancelIntervalRef.current = setInterval(() => {
      setCancelTime(prev => prev - 1);
    }, 1000);
  }, []);

  // если время закончилось, то очищаем память
  useEffect(() => {
    if (
      isCancelButtonActive &&
      (cancelTime <= 0 || Number(order_status) >= 2 || !isAuthenticated)
    ) {
      clearInterval(cancelIntervalRef.current);
      setIsCancelButtonActive(false);
    }
  }, [cancelTime, order_status, isAuthenticated]);

  // закрываем модалку, если вышло время
  useEffect(() => {
    if (isCancelModalOpen && cancelTime === 0) {
      setIsCancelModalOpen(false);
    }
  }, [cancelTime]);

  // если к нам пришло новое оставшее время, отличное от того, что у нас уже записано
  useEffect(() => {
    setTimeLeft(time_left);

    if (time_left) {
      timeLeftIntervalRef.current = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    }

    return () => {
      if (time_left) {
        clearInterval(timeLeftIntervalRef.current);
      }
    };
  }, [time_left]);

  // если время закончилось, то очищаем память
  useEffect(() => {
    if (timeLeft === 0) {
      clearInterval(timeLeftIntervalRef.current);
    }
  }, [timeLeft]);

  // показывать оставшееся время, если заказ не завершен и приходит time_left с бека
  // или заказ оформлен ко времени
  const isTimeleftShown = useMemo(() => {
    return (
      isCancelButtonActive ||
      (!isOrderDone && (time_left !== undefined || wanted_time_with_interval))
    );
  }, [isCancelButtonActive, order_status, delivery_type, time_left, wanted_time_with_interval]);

  const getContent = () => {
    if (!isTimeleftShown) {
      return null;
    }

    // временно отмена работает только для авторизованного пользователя
    // в первую минуту до статуса 2 показываем кнопку отмены
    if (isCancelButtonActive) {
      return (
        <>
          <button
            className={cx('RightPanel', 'RightPanel_cancel', className)}
            onClick={onCancelModalOpen}
            onKeyDown={e => e.key === 'Enter' && onCancelModalOpen()}
            type="button"
          >
            <span>{intl.formatMessage({ id: 'thanksForOrdering.cancelOrder' })}</span>
            :&nbsp;
            <span style={{ color: '#EB5757', minWidth: '40px' }}>
              {`00:${cancelTime > 9 ? cancelTime : `0${cancelTime}`}`}
            </span>
          </button>
        </>
      );
    }

    // если время закончилось, то извиняемся
    if (timeLeft === 0) {
      return (
        <div className={cx('RightPanel', className)}>
          {intl.formatMessage({ id: 'thanksForOrdering.sorryWeAreLate' })}
        </div>
      );
    }

    // таймер
    return (
      <div className={cx('RightPanel', className)}>
        {/* иконка не вмещается при доставке ко времени */}
        {!wanted_time_with_interval && <TimerIcon className={cx('RightPanel__timer')} />}
        {intl.formatMessage({ id: `order.${delivery_type}Time.title` })}
        :&nbsp;
        <span style={{ color: dateColor, fontWeight: 'bold', minWidth: '40px' }}>
          {wanted_time_with_interval || currentDeliveryTime || (
            <div className={cx('RightPanel__spinner')} />
          )}
        </span>
      </div>
    );
  };

  const CancelOrderModal = isRealPhone ? CancelOrderModalMobile : CancelOrderModalDesktop;

  return (
    <>
      {getContent()}
      <CancelOrderModal
        isOpen={isCancelModalOpen}
        onClose={onCancelModalClose}
        order_id={id}
        onSuccessCallback={getOrderStatus}
      />
    </>
  );
}

RightPanel.defaultProps = {
  className: '',
};

RightPanel.propTypes = {
  className: PropTypes.string,
  orderData: PropTypes.object.isRequired,
  getOrderStatus: PropTypes.func.isRequired,
};
